import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageSquare, Plus, Save, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import WaAutomationPanel from "@/components/WaAutomationPanel";

const BUILTIN = {
  mbbs: { name: "MBBS Dashboard", stages: ["New", "Contacted", "Follow-up", "Zoom Done", "Visited", "Docs Recd", "Enrolled", "Closed-lost", "Wrong number"] },
  general: { name: "Leads", stages: ["Untouched", "Not Connected", "Cold", "Warm", "Future Lead", "Closed", "Referred"] },
  counseling: { name: "Counselings", stages: ["New", "Docs Received", "Visited", "Zoom Done", "Options Sent", "Enrolled", "Spoke", "Future Lead", "Closed"] },
};

const TOKENS = ["{name}", "{first_name}", "{country}", "{counsellor}", "{neet}", "{score}"];

const SAMPLE_LEAD = { name: "Riya Sharma", preferred_country: "Georgia", assigned_to_name: "You", neet_score: 540 };

function render(body, lead) {
  const name = lead.name || "there";
  return (body || "")
    .replaceAll("{name}", name)
    .replaceAll("{first_name}", name.split(" ")[0])
    .replaceAll("{country}", lead.preferred_country || "your dream country")
    .replaceAll("{counsellor}", lead.assigned_to_name || "your counsellor")
    .replaceAll("{neet}", String(lead.neet_score ?? ""))
    .replaceAll("{score}", String(lead.test_score ?? ""));
}

const EMPTY = { name: "", stages: [], body: "", media_url: "", media_type: "", active: true };

export default function WaCommandCentreTab() {
  const [pipeline, setPipeline] = useState("general");
  const [customPipelines, setCustomPipelines] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template id or "new"
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pipelines").then((r) => setCustomPipelines(r.data?.pipelines || [])).catch(() => {});
  }, []);

  const pipelineOptions = useMemo(() => {
    const opts = Object.entries(BUILTIN).map(([id, v]) => ({ id, name: v.name, stages: v.stages }));
    customPipelines.forEach((p) =>
      opts.push({ id: p.id, name: p.name, stages: (p.stages || []).map((s) => s.label) }),
    );
    return opts;
  }, [customPipelines]);

  const currentStages = useMemo(
    () => pipelineOptions.find((p) => p.id === pipeline)?.stages || [],
    [pipelineOptions, pipeline],
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/wa-templates?pipeline=${encodeURIComponent(pipeline)}`);
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setEditing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline]);

  const startNew = () => {
    setDraft({ ...EMPTY });
    setEditing("new");
  };
  const startEdit = (t) => {
    setDraft({
      name: t.name, stages: t.stages || [], body: t.body,
      media_url: t.media_url || "", media_type: t.media_type || "", active: t.active !== false,
    });
    setEditing(t.id);
  };

  const toggleStage = (s) =>
    setDraft((d) => ({
      ...d,
      stages: d.stages.includes(s) ? d.stages.filter((x) => x !== s) : [...d.stages, s],
    }));

  const insertToken = (tok) => setDraft((d) => ({ ...d, body: `${d.body}${tok}` }));

  const save = async () => {
    if (!draft.name.trim() || !draft.body.trim()) {
      toast.error("Name and message body are required");
      return;
    }
    setSaving(true);
    const payload = { ...draft, pipeline, media_type: draft.media_type || null, media_url: draft.media_url || null };
    try {
      if (editing === "new") await api.post("/admin/wa-templates", payload);
      else await api.put(`/admin/wa-templates/${editing}`, payload);
      toast.success("Template saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/admin/wa-templates/${t.id}`);
      setTemplates((x) => x.filter((i) => i.id !== t.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="wa-command-centre-tab">
      <WaAutomationPanel />

      <div className="flex items-center gap-3 border-t border-slate-100 pt-5">
        <Label className="text-xs">Pipeline</Label>
        <Select value={pipeline} onValueChange={setPipeline}>
          <SelectTrigger className="h-9 w-56" data-testid="wa-pipeline-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pipelineOptions.map((p) => (
              <SelectItem key={p.id} value={p.id} data-testid={`wa-pipeline-${p.id}`}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {editing === null && (
          <Button size="sm" onClick={startNew} className="ml-auto bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="wa-template-new">
            <Plus className="mr-1.5 h-4 w-4" /> New template
          </Button>
        )}
      </div>

      {editing !== null && (
        <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4" data-testid="wa-template-form">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-[#0A2540]">
              {editing === "new" ? "New template" : "Edit template"}
            </span>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
          </div>
          <div className="space-y-3">
            <Input placeholder="Template name (e.g. Day-1 Welcome)" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-9" data-testid="wa-template-name" />

            <div>
              <Label className="text-xs text-slate-500">Show on stages (none = all stages)</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {currentStages.map((s) => (
                  <button key={s} type="button" onClick={() => toggleStage(s)}
                    className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${draft.stages.includes(s) ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                    data-testid={`wa-stage-${s}`}>{s}</button>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-1 flex flex-wrap items-center gap-1.5">
                <Label className="text-xs text-slate-500">Message</Label>
                {TOKENS.map((t) => (
                  <button key={t} type="button" onClick={() => insertToken(t)} className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] text-[#1877F2] hover:bg-blue-100" data-testid={`wa-token-${t}`}>{t}</button>
                ))}
              </div>
              <Textarea rows={4} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} placeholder="Hi {first_name}, this is {counsellor} from MedAdmits…" className="resize-none" data-testid="wa-template-body" />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Media URL (optional)</Label>
                <Input placeholder="https://… image / video / voice note" value={draft.media_url} onChange={(e) => setDraft({ ...draft, media_url: e.target.value })} className="h-9" data-testid="wa-template-media-url" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Media type</Label>
                <Select value={draft.media_type || "none"} onValueChange={(v) => setDraft({ ...draft, media_type: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="wa-template-media-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="voice">Voice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Live preview */}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
              <div className="mb-1 text-[10px] uppercase tracking-wide text-emerald-700">Preview (sample lead)</div>
              <div className="whitespace-pre-wrap text-xs text-slate-700">{render(draft.body, SAMPLE_LEAD) || "—"}</div>
              {draft.media_url && <div className="mt-1 truncate text-[10px] text-emerald-700">+ {draft.media_type || "media"}: {draft.media_url}</div>}
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} data-testid="wa-template-active" />
              <span className="text-xs text-slate-500">Active</span>
              <Button size="sm" onClick={save} disabled={saving} className="ml-auto bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="wa-template-save">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> Loading…</div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400" data-testid="wa-templates-empty">
          No templates for this pipeline yet.
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`wa-template-card-${t.id}`}>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 shrink-0 text-[#25D366]" />
                <span className="text-sm font-medium text-[#0A2540]">{t.name}</span>
                {!t.active && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">Inactive</span>}
                {t.media_type && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-[#1877F2]">{t.media_type}</span>}
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(t)} data-testid={`wa-template-edit-${t.id}`}>Edit</Button>
                  <button onClick={() => remove(t)} className="rounded-md p-1.5 text-rose-400 hover:bg-rose-50" data-testid={`wa-template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-1 line-clamp-2 text-xs text-slate-500">{t.body}</div>
              {(t.stages || []).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {t.stages.map((s) => <span key={s} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{s}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
